<template>
  <div class="flex flex-col gap-2.5">
    <div v-if="hasSelectedValue" class="glass-tile flex flex-col divide-y divide-border rounded-xl">
      <template v-for="targetType in [ 'geo_locations', 'excluded_geo_locations' ]">
        <div
          v-for="(row, key) in modelValue[targetType].all"
          :key="'geolocation-target-selector--row--' + key + '--' + row.key"
          class="flex min-h-11 items-center gap-3 px-4 py-1.5"
        >
          <loader-circle-icon v-if="rowIsLoading(row)" class="size-4 shrink-0 animate-spin text-primary" />
          <map-pin-icon v-else :class="[ 'size-4 shrink-0', targetType === 'geo_locations' ? 'text-primary' : 'text-destructive' ]" />

          <span class="min-w-0 flex-1 truncate text-sm">
            {{ rowIsLoading(row) ? tr('Loading...') : rowName(row) }}
          </span>

          <distance-radius-selector
            v-if="'radius' in row"
            v-model="row.radius"
            :min="row.radiusMin"
            :max="row.radiusMax"
            :disabled="disabled"
            @update:model-value="() => delayedRedrawFullMap()"
          />

          <ui-button
            v-if="!disabled"
            variant="ghost"
            size="icon"
            class="size-7"
            :aria-label="tr('Remove')"
            @click="() => modelValue[targetType].remove(row.type, row.key)"
          >
            <x-icon />
          </ui-button>
        </div>
      </template>
    </div>

    <div v-if="!disabled" class="flex gap-2.5 max-sm:flex-col">
      <select-field
        v-model="includeOrExclude"
        trigger-class="w-56 max-sm:w-full"
        :options="includeOrExcludeOptions"
        :reduce="(option) => option.value"
        :label="(option) => option.label"
        :aria-label="tr('Include location')"
      >
        <template #option="{ option }">
          <map-pin-icon :class="[ 'size-4 shrink-0', option.value === 'include' ? 'text-primary' : 'text-destructive' ]" />
          {{ option.label }}
        </template>
      </select-field>

      <autosuggest
        v-model="autosuggestInputValue"
        :input-props="{ id: 'autosuggest', placeholder: tr('Search locations') }"
        :suggestions="[{ data: suggestions }]"
        :loading="isLoading"
        @update:model-value="updateSuggestions"
        @selected="onSelectSuggestion"
      >
        <template #default="{ suggestion }">
          {{ getName(suggestion.item) }}
        </template>
      </autosuggest>
    </div>

    <div
      v-show="hasSelectedValue"
      ref="map"
      class="pointer-events-none h-52 overflow-hidden rounded-xl border border-border empty:hidden [&_.gm-style-cc]:hidden [&_.gmnoprint]:hidden"
    />
  </div>
</template>

<script>
import { LoaderCircle, MapPin, X } from '@lucide/vue';
import * as Sentry from '@sentry/browser';

import FacebookTargetingDescriptor from '@/descriptors/FacebookTargeting.js';
import ToolService from '@/services/Tool.js';
import Autosuggest from '@/ui/components/Autosuggest.vue';
import DistanceRadiusSelector from '@/ui/components/DistanceRadiusSelector.vue';
import SelectField from '@/ui/components/SelectField.vue';
import debounce from '@/utils/debounce.js';

export default {
  name: 'GeolocationTargetSelector',
  components: {
    Autosuggest,
    DistanceRadiusSelector,
    LoaderCircleIcon: LoaderCircle,
    MapPinIcon: MapPin,
    SelectField,
    XIcon: X,
  },
  props: {
    modelValue: {
      type: FacebookTargetingDescriptor,
      required: true,
    },
    geolocationsRequired: {
      type: Boolean,
      required: false,
      default: () => true,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: () => false,
    },
  },
  data() {
    return {
      isMounted: false,
      includeOrExclude: 'include',
      autosuggestInputValue: null,
      isLoading: false,
      suggestions: [],
      updateSuggestions: debounce(newValue => {
        if (newValue.trim().length === 0 || !this.isMounted) {
          this.suggestions = [];
          return;
        }

        // Region groups (country_groups) are a fixed local list, not returned by
        // the Facebook geolocation search — merge the matching ones in front of
        // the live results so they appear in the same include/exclude dropdown.
        const groupMatches = this.matchCountryGroups(newValue);

        this.isLoading = true;

        ToolService.facebookAdGeolocationSearch(newValue)
          .then(resp => this.suggestions = [ ...groupMatches, ...resp ])
          .catch(() => this.suggestions = groupMatches)
          .finally(() => this.isLoading = false);
      }),
      delayedRedrawFullMap: debounce(() => {
        this.redrawFullMap();
      }),

    };
  },
  computed: {
    includeOrExcludeOptions() {
      return [
        {
          value: 'include',
          label: this.tr('Include location'),
          icon: 'marker-include',
        },
        {
          value: 'exclude',
          label: this.tr('Exclude location'),
          icon: 'marker-exclude',
        },
      ];
    },
    countryGroupOptions() {
      return [
        { type: 'country_group', key: 'worldwide', name: this.tr('Worldwide') },
        { type: 'country_group', key: 'europe', name: this.tr('Europe') },
        { type: 'country_group', key: 'asia', name: this.tr('Asia') },
        { type: 'country_group', key: 'eea', name: this.tr('EEA') },
        { type: 'country_group', key: 'nafta', name: this.tr('NAFTA') },
        { type: 'country_group', key: 'asean', name: this.tr('ASEAN') },
        { type: 'country_group', key: 'mercosur', name: this.tr('Mercosur') },
        { type: 'country_group', key: 'emerging_markets', name: this.tr('Emerging markets') },
      ];
    },
    selectedValues() {
      return [ ...this.modelValue.geo_locations.all, ...this.modelValue.excluded_geo_locations.all ];
    },
    hasSelectedValue() {
      return this.selectedValues.length !== 0;
    },
    hasWorldwideSelected() {
      return [
        ...this.modelValue.geo_locations.country_groups,
        ...this.modelValue.excluded_geo_locations.country_groups,
      ].some(group => group.key === 'worldwide');
    },
    selectedValuesClone() {
      return this.selectedValues.map(item => ({
        ...JSON.parse(JSON.stringify(item)),
        key: item.key,
      }));
    },
  },
  watch: {
    '$root.language'(val, oldVal) {
      if (val !== oldVal && this.map) {
        this.map = null;
        this.mapCircles = {};
        this.mapMarkers = {};

        this.loadMapsJs({ forceReload: true })
          .then(() => this.reinit())
          /* eslint-disable no-console */
          .catch(err => console.error(err));
      }
    },
    selectedValuesClone: {
      deep: true,
      handler(val, oldVal) {
        this.initMap()
          .then(() => {
            const newKeys = val.map(item => item.key);
            const oldKeys = oldVal.map(item => item.key);

            if (newKeys.length === oldKeys.length) {
              return;
            }

            const removedKeys = oldKeys.filter(x => !newKeys.includes(x));
            removedKeys.forEach(key => {
              if (key in this.mapCircles) {
                this.mapCircles[key].setMap(null);
                delete this.mapCircles[key];
              }

              if (key in this.mapMarkers) {
                this.mapMarkers[key].setMap(null);
                delete this.mapMarkers[key];
              }
            });

            this.loadAllMetadata();
            this.drawCountriesToMap();
            this.fitMap();

            return;
          })
          .catch(err => this.error(err));
      },
    },
  },
  created() {
    // Google Maps objects stay non-reactive: through a Vue proxy setMap(null)
    // does not reach the real overlay, so replaced circles stayed on the map.
    this.map = null;
    this.mapCircles = {};
    this.mapMarkers = {};

    this.loadMapsJs();
  },
  mounted() {
    if (this.hasSelectedValue) {
      this.reinit();
    }

    this.isMounted = true;
  },
  beforeUnmount() {
    this.isMounted = false;
  },
  methods: {
    loadMapsJs({ forceReload = false } = {}) {
      return new Promise((resolve, reject) => {
        if (!forceReload) {
          if (window.google && window.google.maps && window.google.maps.MapTypeId) {
            resolve();
            return;
          }

          const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
          if (existingScript) {
            resolve();
            return;
          }
        }
        else {
          const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
          if (existingScript) {
            existingScript.remove();
          }
          if (window.google && window.google.maps) {
            delete window.google.maps;
          }
        }

        const mapsScript = document.createElement('script');
        mapsScript.async = true;
        mapsScript.src = 'https://maps.googleapis.com/maps/api/js?key=AIzaSyB_oVlad22d70FK3nqBRAznShA_NFH-oKU&loading=async&language=' + this.$root.language;
        mapsScript.onload = () => resolve();
        mapsScript.onerror = err => reject(err);
        document.head.appendChild(mapsScript);
      });
    },
    reinit() {
      this.initMap()
        .then(() => this.loadAllMetadata())
        .then(() => this.redrawFullMap())
        .catch(err => this.error(err));
    },
    loadAllMetadata() {
      return new Promise(resolve => {
        if (!this.hasSelectedValue || !this.isMounted) {
          resolve();
          return;
        }

        let processed = 0;
        this.selectedValues.forEach(item => {
          if (item.type === 'custom_location') {
            ++processed;
            if (processed === this.selectedValues.length) {
              resolve();
              this.$forceUpdate();
            }
            return;
          }

          ToolService.facebookAdGeolocationMetadata(item.type, item.key)
            .then(resp => item.meta = resp)
            .catch(err => {
              // A region group's label comes from countryGroupOptions (see
              // rowName), so a failed fetch only costs the map shading — don't
              // spam an error toast for it. Other types still surface the error.
              if (item.type !== 'country_group') {
                this.error(err);
              }
            })
            .finally(() => {
              ++processed;
              if (processed === this.selectedValues.length) {
                resolve();
                this.$forceUpdate();
              }
            });
        });
      });
    },
    onSelectSuggestion(suggestion) {
      if (suggestion && suggestion.item) {
        const key = this.includeOrExclude === 'include' ? 'geo_locations' : 'excluded_geo_locations';

        const addedItem = this.modelValue[key].add(suggestion.item.type, suggestion.item.key);
        if (addedItem) {
          addedItem.meta.name = suggestion.item.name;

          if (addedItem.type === 'country_group') {
            // The row label comes from countryGroupOptions (see rowName); the
            // metadata fetch only brings in country_codes so the group's member
            // countries can be shaded on the map and used for overlap detection.
            ToolService.facebookAdGeolocationMetadata(addedItem.type, addedItem.key)
              .then(resp => {
                addedItem.meta = resp;

                this.drawCountriesToMap();
                this.fitMap();

                return resp;
              })
              // Best-effort: the label still renders without member countries.
              .catch(e => this.error(e))
              .finally(() => this.validate());
          }
          else {
            ToolService.facebookAdGeolocationMetadata(addedItem.type, addedItem.key)
              .then(resp => {
                addedItem.meta = resp;

                this.drawToMap(this.includeOrExclude, addedItem);
                this.fitMap();

                this.validate();

                return resp;
              })
              .catch(err => this.error(err));
          }
        }
      }

      this.autosuggestInputValue = null;
      this.suggestions = [];
    },
    initMap() {
      return new Promise(resolve => {
        if (this.map !== null) {
          resolve();
          return;
        }

        const waitForGoogle = () => {
          if (
            !('google' in window)
            || !('maps' in window.google)
            || !('MapTypeId' in window.google.maps)
          ) {
            setTimeout(() => waitForGoogle(), 25);
            return;
          }

          this.$nextTick(() => {
            if (this.hasSelectedValue && this.$refs.map) {
              this.map = new window.google.maps.Map(this.$refs.map, {
                styles: require('@/assets/googleMapsStyle.json'),
                mapTypeId: window.google.maps.MapTypeId.ROADMAP,
                disableDefaultUI: true,
                draggable: false,
                zoom: 1,
                center: { lat: 0, lng: 0 },
              });

              this.map.data.addGeoJson(require('@/assets/geo.json'));
              this.drawCountriesToMap();
            }

            resolve();
          });
        };
        waitForGoogle();
      });
    },
    drawToMap(targetType, item) {
      if (item.type === 'country_group') {
        // Region groups have no coordinates or country polygon — nothing to draw.
        return;
      }

      if ('radius' in item) {
        this.drawCircleToMap(targetType, item);
      }
      else if (item.type === 'country') {
        this.drawCountriesToMap();
      }
      else {
        this.drawMarkerToMap(targetType, item);
      }
    },
    drawCircleToMap(targetType, item) {
      const lat = parseFloat(item.meta.latitude);
      const lng = parseFloat(item.meta.longitude);
      if (!lat || !lng) {
        Sentry.captureMessage('Invalid location data in GeolocationTargetSelector', {
          extra: { item },
        });

        return;
      }

      const color = targetType === 'include' ? '#2400ff' : '#F04D4D';

      const center = new window.google.maps.LatLng(lat, lng);
      // Redraws (e.g. while the radius changes) replace the previous circle.
      this.mapCircles[item.key]?.setMap(null);
      this.mapCircles[item.key] = new window.google.maps.Circle({
        strokeColor: color,
        strokeOpacity: 0.4,
        strokeWeight: 2,
        fillColor: color,
        fillOpacity: 0.15,
        map: this.map,
        center,
        // Meta treats the value as a radius in km.
        radius: item.radius * 1000,
      });

      this.drawMarkerToMap(targetType, item);
    },
    drawMarkerToMap(targetType, item) {
      const lat = parseFloat(item.meta.latitude);
      const lng = parseFloat(item.meta.longitude);
      if (!lat || !lng) {
        Sentry.captureMessage('Invalid location data in GeolocationTargetSelector', {
          extra: { item },
        });

        return;
      }

      let iconUrl = null;
      if (targetType === 'include') {
        iconUrl = require('@/assets/img/icons/marker-include.svg');
      }
      else {
        iconUrl = require('@/assets/img/icons/marker-exclude.svg');
      }

      const center = new window.google.maps.LatLng(lat, lng);
      this.mapMarkers[item.key]?.setMap(null);
      this.mapMarkers[item.key] = new window.google.maps.Marker({
        map: this.map,
        position: center,
        icon: {
          url: iconUrl,
          scaledSize: new window.google.maps.Size(32, 32),
        },
      });
    },
    countriesForMap(geoLocations) {
      // Direct country targets plus the member countries of any selected region
      // group (country_groups carry their ISO-2 members in meta.country_codes),
      // so a group shades all of its countries on the map.
      return [
        ...geoLocations.countries.map(item => item.key),
        ...geoLocations.country_groups.flatMap(item => item.meta.country_codes || []),
      ];
    },
    drawCountriesToMap() {
      if (!this.map) {
        return;
      }

      const includedCountries = this.countriesForMap(this.modelValue.geo_locations);
      const excludedCountries = this.countriesForMap(this.modelValue.excluded_geo_locations);

      this.map.data.setStyle(feature => {
        let color = 'transparent';
        if (includedCountries.includes(feature.getProperty('wb_a2'))) {
          color = 'blue';
        }
        if (excludedCountries.includes(feature.getProperty('wb_a2'))) {
          color = 'red';
        }

        return /** @type {google.maps.Data.StyleOptions} */({
          fillColor: color,
          strokeColor: color,
          strokeWeight: 2,
        });
      });
    },
    clearMap() {
      Object.keys(this.mapCircles).forEach(key => {
        this.mapCircles[key].setMap(null);
        delete this.mapCircles[key];
      });

      Object.keys(this.mapMarkers).forEach(key => {
        this.mapMarkers[key].setMap(null);
        delete this.mapMarkers[key];
      });
    },
    redrawFullMap() {
      if (!this.isMounted) {
        return;
      }

      this.clearMap();

      this.drawCountriesToMap();
      this.modelValue.geo_locations.all.forEach(item => this.drawToMap('include', item));
      this.modelValue.excluded_geo_locations.all.forEach(item => this.drawToMap('exclude', item));

      this.fitMap();
    },
    fitMap() {
      if (!this.map) {
        return;
      }

      // "Worldwide" targets every country, so fitting bounds over all country
      // polygons is meaningless and actually breaks: the antimeridian-crossing
      // countries (Russia, Fiji, …) collapse the longitude span to a thin sliver
      // around 180°, which renders as a stray line. Just show the whole-world view.
      if (this.hasWorldwideSelected) {
        this.map.setZoom(1);
        this.map.setCenter({ lat: 0, lng: 0 });
        return;
      }

      const bounds = new window.google.maps.LatLngBounds();
      Object.values(this.mapCircles).forEach(circle => {
        const circleBounds = circle.getBounds();

        bounds.extend(circleBounds.getNorthEast());
        bounds.extend(circleBounds.getSouthWest());
      });
      Object.values(this.mapMarkers).forEach(marker =>  bounds.extend(marker.position));

      const includedCountries = this.countriesForMap(this.modelValue.geo_locations);
      const excludedCountries = this.countriesForMap(this.modelValue.excluded_geo_locations);

      this.map.data.forEach(feature => {
        const countryCode = feature.getProperty('wb_a2');
        if (
          includedCountries.includes(countryCode)
          || excludedCountries.includes(countryCode)
        ) {
          feature.getGeometry().forEachLatLng(coords => bounds.extend(coords));
        }
      });

      if (!bounds.isEmpty()) {
        this.map.fitBounds(bounds, 0);
      }
    },
    getName(locationMeta) {
      if (!locationMeta) {
        return '';
      }

      let result = locationMeta.name;
      if ('address_string' in locationMeta) {
        result += ', ' + locationMeta.address_string;
      }
      else if ('primary_city' in locationMeta) {
        result += ', ' + locationMeta.primary_city;
      }
      else if ('region' in locationMeta && locationMeta.region !== locationMeta.name) {
        result += ', ' + locationMeta.region;
      }

      return result;
    },
    countryGroupLabel(key) {
      const option = this.countryGroupOptions.find(item => item.key === key);

      return option ? option.name : key;
    },
    rowIsLoading(row) {
      // Region groups carry no Facebook metadata, so they are never "loading" —
      // their label comes straight from the localized option list.
      if (row.type === 'country_group') {
        return false;
      }

      // A custom_location fetches no metadata (its coordinates are already stored),
      // so it must never sit in the "loading" state even if it has no name.
      if (row.type === 'custom_location') {
        return false;
      }

      return !row.meta.name;
    },
    rowName(row) {
      // Render the live, localized label for a region group so it re-localizes
      // on a language switch (countryGroupOptions is reactive to $root.language),
      // instead of relying on the name frozen into meta at add/hydrate time.
      if (row.type === 'country_group') {
        return this.countryGroupLabel(row.key);
      }

      return this.getName(row.meta);
    },
    matchCountryGroups(query) {
      const normalized = query.trim().toLowerCase();
      if (!normalized) {
        return [];
      }

      const selectedKeys = [
        ...this.modelValue.geo_locations.country_groups,
        ...this.modelValue.excluded_geo_locations.country_groups,
      ].map(item => item.key);

      return this.countryGroupOptions.filter(option => {
        if (selectedKeys.includes(option.key)) {
          return false;
        }

        return option.name.toLowerCase().includes(normalized)
          || option.key.toLowerCase().includes(normalized);
      });
    },
    validate() {
      if (this.geolocationsRequired && this.modelValue.geo_locations.all.length === 0) {
        this.error(this.tr('The target location must be defined.'));
        return false;
      }
      if (this.modelValue.hasInvalidGeoExclude) {
        this.error(this.tr('There is an excluded location that is not part of a larger, selected targeted area.'));
        return false;
      }
      if (this.modelValue.hasGeoOverlaps) {
        this.error(this.tr('There is overlap among the selected target locations. It is necessary to eliminate the overlap to proceed.'));
        return false;
      }

      return true;
    },
  },
};
</script>
